import { getCartRange, isChartWithCategory } from '@app/core/services/charts/plugins/tools/chart-helper';
import { createDebounce, IDebounceController } from '@app/shared/utils/debounce-controller';
import { roundToTwoDecimals } from '@app/shared/utils/math';
import { Chart, Plugin } from 'chart.js';
import equal from 'fast-deep-equal';

export interface IScrollPanOptions {
  /**
   * @description Default value is true
   */
  display?: boolean;
  init?: {
    minTickWidth: number;
  };
}

interface IScrollPanState {
  thumbCleanup: (() => void) | null;
  ignoreOneThumbContainerEvents: boolean;
  updateThumbDebounce: IDebounceController;
  setResizeScaleDebounce: IDebounceController;
  prevLabels: unknown[];
  prevDatasets: unknown[];
  allMin?: number;
  allMax?: number;
  lastMin?: number;
  lastMax?: number;
  isZooming?: boolean;
  isResizing?: boolean;
  thumbWrapper?: HTMLDivElement | null;
  thumb?: HTMLDivElement | null;
  resizeDebounce: IDebounceController;
}

declare module 'chart.js' {
  enum UpdateModeEnum {
    scroll = 'scroll',
  }

  interface Chart {
    _scrollPanPlugin?: IScrollPanState;
  }

  interface PluginOptionsByType {
    scrollPan?: IScrollPanOptions;
  }
}

export const scrollPanPlugin: Plugin = {
  id: 'scrollPan',

  afterInit(chart: Chart) {
    if (isPluginDeactivated(chart)) return;

    chart._scrollPanPlugin = getDefaultScrollPanPlugin();

    createThumbElements(chart);
    createEventsListeners(chart);
  },
  beforeUpdate(chart: Chart, args) {
    if (isPluginDeactivated(chart)) return;

    if (args['mode'] === 'resize') {
      setResizeProgressState(chart, true);
    }

    if (args['mode'] === 'zoom') {
      setZoomProgressState(chart, true);
    }
  },
  afterLayout(chart: Chart) {
    if (isPluginDeactivated(chart)) return;

    const updateNeeded = setDefaultScroll(chart);

    if (updateNeeded) {
      chart.update('none');
    } else if (!isZoomInProgress(chart)) {
      restoreLastScale(chart, true);
    }

    if (isResizeInProgress(chart)) {
      setResizeScaleWithDebounce(chart);
    }
  },
  afterUpdate(chart: Chart, args) {
    if (isPluginDeactivated(chart)) return;

    if (args['mode'] === 'resize') {
      setResizeProgressState(chart, false);
    }

    if (args['mode'] === 'zoom') {
      saveCurrentScale(chart);
      setZoomProgressState(chart, false);
    }

    updateThumbWithDebounce(chart);
  },
  resize(chart: Chart) {
    // fix for line chart to fix issue with overlapping bg
    if (isPluginDeactivated(chart)) return;

    const resizeDebounce = chart._scrollPanPlugin?.resizeDebounce || null;

    if (!resizeDebounce) return;

    resizeDebounce.run(() => chart.update('none'));
  },
  beforeDestroy(chart: Chart) {
    if (isPluginDeactivated(chart)) return;

    if (!chart._scrollPanPlugin) return;

    if (chart._scrollPanPlugin.thumbCleanup) {
      chart._scrollPanPlugin.thumbCleanup();
    }

    chart._scrollPanPlugin = getDefaultScrollPanPlugin();
  },
};

function isPluginDeactivated(chart: Chart): boolean {
  const options = chart.options.plugins?.scrollPan as IScrollPanOptions | undefined;

  return !(options?.display ?? true);
}

// helpers
function updateThumbWithDebounce(chart: Chart) {
  if (!chart._scrollPanPlugin) return;

  chart._scrollPanPlugin.updateThumbDebounce.run(() => updateThumb(chart));
}

function updateThumb(chart: Chart) {
  if (isChartWithCategory(chart)) {
    updateThumbForCategoryChart(chart);
  } else {
    updateThumbForLinearChart(chart);
  }
}

function updateThumbForCategoryChart(chart: Chart) {
  if (!(chart._scrollPanPlugin && chart._scrollPanPlugin.thumb)) return;

  const thumb: HTMLDivElement = chart._scrollPanPlugin.thumb;
  const thumbContainer = thumb.parentElement as HTMLDivElement;
  const { width } = thumbContainer.getBoundingClientRect();

  if (!thumbContainer) return;

  const xScale = chart.scales['x'];

  if (!xScale) return;

  const total = chart.data.labels?.length ?? 0;
  const min = xScale.min ?? 0;
  const visible = (xScale.max || 0) - min + 1;

  if (total <= 0 || visible <= 0) {
    thumb.style.width = '100%';
    return;
  }

  const widthRatio = total / visible;
  thumb.style.width = `${width * widthRatio}px`;

  const newScrollLeft = ((width * widthRatio) / total) * min;

  chart._scrollPanPlugin.ignoreOneThumbContainerEvents =
    chart._scrollPanPlugin.ignoreOneThumbContainerEvents || thumbContainer.scrollLeft !== newScrollLeft;

  thumbContainer.scrollLeft = newScrollLeft;
}

function updateThumbForLinearChart(chart: Chart) {
  if (!(chart._scrollPanPlugin && chart._scrollPanPlugin.thumb)) return;

  const thumb: HTMLDivElement = chart._scrollPanPlugin.thumb;
  const thumbContainer = thumb.parentElement as HTMLDivElement;
  const { width } = thumbContainer.getBoundingClientRect();

  if (!thumbContainer) return;

  const xScale = chart.scales['x'];

  if (!xScale) return;

  const initMin = chart._scrollPanPlugin.allMin || 0;
  const initMax = chart._scrollPanPlugin.allMax || 0;

  const total = initMax - initMin;

  const min = xScale.min ?? 0;
  const max = xScale.max ?? 0;
  const visible = max - min;

  if (min <= initMin && max >= initMax) {
    thumb.style.width = '100%';
    return;
  }
  const widthRatio = total / visible;
  const containerWidth = width * widthRatio;
  thumb.style.width = `${containerWidth}px`;

  const hiddenFromLeft = min - initMin;

  const newScrollLeft = (containerWidth / total) * hiddenFromLeft;

  chart._scrollPanPlugin.ignoreOneThumbContainerEvents =
    chart._scrollPanPlugin.ignoreOneThumbContainerEvents || thumbContainer.scrollLeft !== newScrollLeft;

  thumbContainer.scrollLeft = newScrollLeft;
}

function setResizeScaleWithDebounce(chart: Chart) {
  if (!chart._scrollPanPlugin) return;

  chart._scrollPanPlugin.setResizeScaleDebounce.run(() => setResizeScale(chart));
}

function setResizeScale(chart: Chart) {
  if (isChartWithCategory(chart)) {
    setResizeScaleForCategoryChart(chart);
  } else {
    setResizeScaleForLinearChart(chart);
  }
}

function setResizeScaleForCategoryChart(chart: Chart) {
  const options = chart.options.plugins?.scrollPan as IScrollPanOptions | undefined;
  const lastMin: number = chart._scrollPanPlugin?.lastMin || 0;
  const lastMax: number = chart._scrollPanPlugin?.lastMax || 0;
  const visible = lastMax - lastMin + 1;

  if (!options || !options.init) return;

  const { minTickWidth } = options.init;
  const total = chart.data.labels?.length ?? 0;

  const xScale = chart.scales['x'];

  if (!xScale) return;

  const chartWidth = chart.width;
  const maxSmallTickElements = Math.floor(chartWidth / minTickWidth);

  if (maxSmallTickElements >= visible) return;

  let newMax = lastMax;
  const newMin = lastMin;

  const hiddenLeft = total - lastMax + 1;

  const needToHide = visible - maxSmallTickElements;

  if (hiddenLeft >= needToHide) {
    newMax = lastMax - needToHide;
  }

  if (newMin >= newMax) {
    newMax = newMin + 1;
  }

  // todo: in future add saving scale and increase elements

  setScale(chart, newMin, newMax);

  chart.update('none');
}

function setResizeScaleForLinearChart(chart: Chart) {
  const options = chart.options.plugins?.scrollPan as IScrollPanOptions | undefined;
  const lastMin: number = chart._scrollPanPlugin?.lastMin || 0;
  const lastMax: number = chart._scrollPanPlugin?.lastMax || 0;
  const visible = lastMax - lastMin + 1;

  if (!options || !options.init) return;

  const { minTickWidth } = options.init;

  const initMin = chart._scrollPanPlugin?.allMin || 0;
  const initMax = chart._scrollPanPlugin?.allMax || 0;

  const total = initMax - initMin;

  const xScale = chart.scales['x'];

  if (!xScale) return;

  const chartWidth = chart.width;
  const maxSmallTickElements = chartWidth / minTickWidth;

  if (maxSmallTickElements >= visible) return;

  let newMax = lastMax;
  const newMin = lastMin;

  const hiddenLeft = total - lastMax + 1;

  const needToHide = visible - maxSmallTickElements;

  if (hiddenLeft >= needToHide) {
    newMax = lastMax - needToHide;
  }

  if (newMin >= newMax) {
    newMax = newMin + 1;
  }

  setScale(chart, newMin, newMax);

  chart.update('none');
}

function setInitScale(chart: Chart) {
  if (isChartWithCategory(chart)) {
    setInitScaleForCategoryChart(chart);
  } else {
    setInitScaleForLinearChart(chart);
  }
}

function setInitScaleForCategoryChart(chart: Chart) {
  const options = chart.options.plugins?.scrollPan as IScrollPanOptions | undefined;

  if (!options || !options.init) return;

  const { minTickWidth } = options.init;
  const total = chart.data.labels?.length ?? 0;

  const xScale = chart.scales['x'];

  if (!xScale) return;

  const chartWidth = chart.width;
  const maxSmallTickElements = Math.floor(chartWidth / minTickWidth);
  const isExistingPlaceForAllData = total < maxSmallTickElements;

  if (isExistingPlaceForAllData) {
    setScale(chart, 0, total - 1);
    return;
  }

  setScale(chart, 0, maxSmallTickElements - 1);
}

function setInitScaleForLinearChart(chart: Chart) {
  const options = chart.options.plugins?.scrollPan as IScrollPanOptions | undefined;

  if (chart._scrollPanPlugin) {
    const { min, max } = getCartRange(chart);

    chart._scrollPanPlugin.allMin = min;
    chart._scrollPanPlugin.allMax = max;
  }

  if (!options || !options.init) return;

  const { minTickWidth } = options.init;

  const initMin = chart._scrollPanPlugin?.allMin || 0;
  const initMax = chart._scrollPanPlugin?.allMax || 0;

  const total = initMax - initMin;

  const xScale = chart.scales['x'];

  if (!xScale) return;

  const chartWidth = chart.width;
  const visibleArea = chartWidth / minTickWidth;
  const isExistingPlaceForAllData = total < visibleArea;

  if (isExistingPlaceForAllData) {
    setScale(chart, initMin, initMax);
    return;
  }

  setScale(chart, initMin, initMin + visibleArea - 1);
}

function isNewData(chart: Chart) {
  if (!chart._scrollPanPlugin) return true;

  return !equal(chart._scrollPanPlugin.prevDatasets, chart.data.datasets[0].data);
  // return chart._scrollPanPlugin.prevLabels.length !== chart.data.labels?.length;
}

function isZoomInProgress(chart: Chart): boolean {
  if (!chart._scrollPanPlugin) return false;

  return !!chart._scrollPanPlugin.isZooming;
}

function setZoomProgressState(chart: Chart, value: boolean) {
  if (!chart._scrollPanPlugin) return;

  chart._scrollPanPlugin.isZooming = value;
}

function isResizeInProgress(chart: Chart): boolean {
  if (!chart._scrollPanPlugin) return false;

  return !!chart._scrollPanPlugin.isResizing;
}

function setResizeProgressState(chart: Chart, value: boolean) {
  if (!chart._scrollPanPlugin) return;

  chart._scrollPanPlugin.isResizing = value;
}

function setDefaultScroll(chart: Chart): boolean {
  if (!isNewData(chart)) return false;

  if (!chart._scrollPanPlugin) return false;

  chart._scrollPanPlugin!.prevLabels = chart.data.labels || [];
  chart._scrollPanPlugin!.prevDatasets = chart.data.datasets[0].data || [];

  setInitScale(chart);
  return true;
}

function saveCurrentScale(chart: Chart, min?: number, max?: number) {
  const xScale = chart.scales['x'];

  if (!xScale) return;

  if (chart._scrollPanPlugin) {
    chart._scrollPanPlugin.lastMin = min !== undefined ? min : xScale.min;
    chart._scrollPanPlugin.lastMax = max !== undefined ? max : xScale.max;
  }
}

function setScale(chart: Chart, min: number, max: number) {
  if (chart.options.scales && chart.options.scales['x']) {
    const ignoreRounding = isChartWithCategory(chart);

    const newMin = ignoreRounding ? min : roundToTwoDecimals(min) || undefined;
    const newMax = ignoreRounding ? max : roundToTwoDecimals(max) || undefined;

    chart.options.scales['x'].min = newMin;
    chart.options.scales['x'].max = newMax;

    saveCurrentScale(chart, newMin, newMax);
  }
}

function restoreLastScale(chart: Chart, needToUpdate = false) {
  const xScale = chart.scales['x'];

  if (
    chart._scrollPanPlugin &&
    chart._scrollPanPlugin.lastMin !== undefined &&
    chart._scrollPanPlugin.lastMax !== undefined &&
    xScale &&
    (xScale.min !== chart._scrollPanPlugin.lastMin || xScale.max !== chart._scrollPanPlugin.lastMax)
  ) {
    setScale(chart, chart._scrollPanPlugin.lastMin, chart._scrollPanPlugin.lastMax);

    if (needToUpdate) {
      chart.update('none');
    }
  }
}

function createThumbElements(chart: Chart) {
  if (!chart._scrollPanPlugin) return;

  const wrapper = chart.canvas.parentElement;
  if (!wrapper) return;

  wrapper.style.position = 'relative';
  wrapper.style.paddingBottom = '0.3125rem';

  const thumbContainer = document.createElement('div');
  thumbContainer.classList.add('chart-scrollbar-wrapper');
  thumbContainer.style.position = 'absolute';
  thumbContainer.style.bottom = '0';
  thumbContainer.style.left = '0';
  thumbContainer.style.width = '100%';
  thumbContainer.style.height = '0.625rem';
  thumbContainer.style.overflow = 'auto';
  thumbContainer.style.overflowY = 'hidden';
  wrapper.appendChild(thumbContainer);

  // fake full-width element to enable scrolling
  const thumb = document.createElement('div');
  thumb.classList.add('chart-scrollbar-thumb');
  thumb.style.height = '1px';
  thumb.style.background = 'transparent';
  thumb.style.position = 'relative';
  thumbContainer.appendChild(thumb);

  chart._scrollPanPlugin.thumbWrapper = thumbContainer;
  chart._scrollPanPlugin.thumb = thumb;
}

function calculateScrollThumbContainer(
  chart: Chart,
  scrollWidth: number,
  scrollLeft: number
): { newMinIndex: number; newMaxIndex: number } | null {
  if (isChartWithCategory(chart)) {
    return calculateScrollThumbContainerForCategoryChart(chart, scrollWidth, scrollLeft);
  } else {
    return calculateScrollThumbContainerForLinearChart(chart, scrollWidth, scrollLeft);
  }
}

function calculateScrollThumbContainerForCategoryChart(
  chart: Chart,
  scrollWidth: number,
  scrollLeft: number
): { newMinIndex: number; newMaxIndex: number } | null {
  const xScale = chart.scales['x'];
  if (!xScale) {
    return null;
  }

  const total = chart.data.labels?.length ?? 0;
  const visible = (xScale.max || 0) - (xScale.min || 0) + 1;

  const singleBarWidthOnScroll = scrollWidth / total;

  let newMinIndex = Math.round(scrollLeft / singleBarWidthOnScroll);
  let newMaxIndex = visible + newMinIndex - 1;

  if (newMinIndex <= 0) {
    newMinIndex = 0;
    newMaxIndex = visible - 1;
  }

  if (newMaxIndex >= total) {
    newMaxIndex = total;
    newMinIndex = total - visible + 1;
  }

  return { newMinIndex, newMaxIndex };
}

function calculateScrollThumbContainerForLinearChart(
  chart: Chart,
  scrollWidth: number,
  scrollLeft: number
): { newMinIndex: number; newMaxIndex: number } | null {
  const xScale = chart.scales['x'];
  if (!xScale || !chart._scrollPanPlugin) {
    return null;
  }

  const initMin = chart._scrollPanPlugin.allMin || 0;
  const initMax = chart._scrollPanPlugin.allMax || 0;

  const total = initMax - initMin;

  const visible = (xScale.max || 0) - (xScale.min || 0);

  const singleDataWidthOnScroll = scrollWidth / total;

  const sideIndent = scrollLeft / singleDataWidthOnScroll;

  let newMinIndex = initMin + sideIndent;
  let newMaxIndex = visible + newMinIndex;

  if (newMinIndex <= initMin) {
    newMinIndex = initMin;
    newMaxIndex = newMinIndex + visible;
  }

  if (newMaxIndex >= initMax) {
    newMaxIndex = initMax;
    newMinIndex = newMaxIndex - visible;
  }

  return { newMinIndex, newMaxIndex };
}

function calculateMoveDrag(
  chart: Chart,
  dx: number,
  startMin: number,
  startVisibleElements: number
): { newMinIndex: number; newMaxIndex: number } | null {
  if (isChartWithCategory(chart)) {
    return calculateMoveDragForCategoryChart(chart, dx, startMin, startVisibleElements);
  } else {
    return calculateMoveDragForLinearChart(chart, dx, startMin, startVisibleElements);
  }
}

function calculateMoveDragForCategoryChart(
  chart: Chart,
  dx: number,
  startMin: number,
  startVisibleElements: number
): { newMinIndex: number; newMaxIndex: number } | null {
  const xScale = chart.scales['x'];
  if (!xScale) return null;

  if (startVisibleElements <= 0) return null;

  const singleBarWidthOnScroll = xScale.width / startVisibleElements;
  const shift = Math.round(dx / singleBarWidthOnScroll);

  let newMinIndex = startMin - shift;
  let newMaxIndex = newMinIndex + startVisibleElements - 1;

  const total = chart.data.labels?.length ?? 0;
  const lastMaxIndex = total - 1;

  if (newMinIndex <= 0) {
    newMinIndex = 0;
    newMaxIndex = startVisibleElements - 1;
  }

  if (newMaxIndex >= lastMaxIndex) {
    newMaxIndex = lastMaxIndex;
    newMinIndex = lastMaxIndex - startVisibleElements + 1;
  }

  return { newMinIndex, newMaxIndex };
}

function calculateMoveDragForLinearChart(
  chart: Chart,
  dx: number,
  startMin: number,
  startVisibleElements: number
): { newMinIndex: number; newMaxIndex: number } | null {
  const xScale = chart.scales['x'];
  if (!xScale) return null;

  if (startVisibleElements <= 0) return null;

  const singleDataWidthOnScroll = xScale.width / startVisibleElements;
  const shift = dx / singleDataWidthOnScroll;

  let newMinIndex = startMin - shift;
  let newMaxIndex = newMinIndex + startVisibleElements - 1;

  const initMin = chart._scrollPanPlugin?.allMin || 0;
  const initMax = chart._scrollPanPlugin?.allMax || 0;

  if (newMinIndex <= initMin) {
    newMinIndex = initMin;
    newMaxIndex = newMinIndex + startVisibleElements - 1;
  }

  if (newMaxIndex >= initMax) {
    newMaxIndex = initMax;
    newMinIndex = newMaxIndex - startVisibleElements + 1;
  }

  return { newMinIndex, newMaxIndex };
}

function createEventsListeners(chart: Chart) {
  if (!chart._scrollPanPlugin) return;

  const scrollThumbContainer = (event: Event) => {
    if (!chart._scrollPanPlugin) return;

    if (chart._scrollPanPlugin.ignoreOneThumbContainerEvents) {
      chart._scrollPanPlugin.ignoreOneThumbContainerEvents = false;
      return;
    }

    const thumbContainer = event.target as HTMLDivElement;
    const scrollLeft = thumbContainer.scrollLeft;
    const scrollWidth = thumbContainer.scrollWidth;

    const newIndexes = calculateScrollThumbContainer(chart, scrollWidth, scrollLeft);

    if (!newIndexes) return;

    if (chart.tooltip) {
      chart.tooltip.setActiveElements([], { x: 0, y: 0 });
    }

    setScale(chart, newIndexes.newMinIndex, newIndexes.newMaxIndex);

    chart.update('none');
  };

  chart._scrollPanPlugin!.thumbWrapper!.addEventListener('scroll', scrollThumbContainer);

  // drag variables
  let isDragging = false;
  let startX = 0;
  let startMin = 0;
  let startVisibleElements = 0;

  const startDrag = (clientX: number) => {
    const xScale = chart.scales['x'];
    if (!xScale) return;
    isDragging = true;
    startX = clientX;
    startMin = xScale.min ?? 0;
    startVisibleElements = (xScale.max || 0) - startMin + 1;
  };

  const moveDrag = (clientX: number) => {
    if (!isDragging) return;
    const dx = clientX - startX;

    const newIndexes = calculateMoveDrag(chart, dx, startMin, startVisibleElements);

    if (!newIndexes) return;

    if (chart.tooltip) {
      chart.tooltip.setActiveElements([], { x: 0, y: 0 });
    }

    setScale(chart, newIndexes.newMinIndex, newIndexes.newMaxIndex);

    chart.update('none');
    updateThumb(chart);
  };

  const endDrag = () => {
    isDragging = false;
  };

  // Mouse events
  const mouseDown = (e: MouseEvent) => startDrag(e.clientX);
  const mouseMove = (e: MouseEvent) => moveDrag(e.clientX);
  const mouseUp = () => endDrag();

  // Touch events
  const touchStart = (e: TouchEvent) => {
    if (e.touches.length === 1) startDrag(e.touches[0].clientX);
  };
  const touchMove = (e: TouchEvent) => {
    if (e.touches.length === 1) moveDrag(e.touches[0].clientX);
  };
  const touchEnd = () => endDrag();

  chart.canvas.addEventListener('mousedown', mouseDown);
  window.addEventListener('mousemove', mouseMove);
  window.addEventListener('mouseup', mouseUp);

  chart.canvas.addEventListener('touchstart', touchStart);
  window.addEventListener('touchmove', touchMove);
  window.addEventListener('touchend', touchEnd);

  // cleanup function
  chart._scrollPanPlugin!.thumbCleanup = () => {
    chart.canvas.removeEventListener('mousedown', mouseDown);
    window.removeEventListener('mousemove', mouseMove);
    window.removeEventListener('mouseup', mouseUp);

    chart.canvas.removeEventListener('touchstart', touchStart);
    window.removeEventListener('touchmove', touchMove);
    window.removeEventListener('touchend', touchEnd);

    chart._scrollPanPlugin!.updateThumbDebounce.cancel();
    chart._scrollPanPlugin!.setResizeScaleDebounce.cancel();
    chart._scrollPanPlugin!.resizeDebounce.cancel();

    if (chart._scrollPanPlugin!.thumbWrapper) {
      chart._scrollPanPlugin!.thumbWrapper.removeEventListener('scroll', scrollThumbContainer);

      if (chart._scrollPanPlugin!.thumbWrapper.parentNode) {
        chart._scrollPanPlugin!.thumbWrapper.parentNode.removeChild(chart._scrollPanPlugin!.thumbWrapper);
      }
      chart._scrollPanPlugin!.thumbWrapper = null;
      chart._scrollPanPlugin!.thumb = null;
    }
  };
}

function getDefaultScrollPanPlugin(): IScrollPanState {
  return {
    thumbCleanup: null,
    ignoreOneThumbContainerEvents: false,
    updateThumbDebounce: createDebounce(100),
    setResizeScaleDebounce: createDebounce(100),
    resizeDebounce: createDebounce(200),
    prevLabels: [],
    prevDatasets: [],
    allMin: 0,
    thumbWrapper: null,
    thumb: null,
  };
}
